// =============================================
// serverDocStore — client for the server-side document blob store (/api/dms/blob)
// =============================================
// New uploads push their gzip-base64 bytes to the server blob volume the moment
// browser processing finishes, so the durable copy lives on the SERVER (not just
// in the browser/IndexedDB). The dms_documents row keeps a `blob:` pointer.
//
//   file_path = 'blob:docs/<acct>/<uuid>.b64'   → fetched here via /api/dms/blob
//   file_path = 'https://...'                   → a real remote URL (future Cloud
//                                                 Storage); fetched directly
//   file_path = 'idb://...'                     → local-only fallback (see docStore)
//
// This sits beside src/lib/docStore.ts (the IndexedDB fallback) — docStorage.ts
// chooses server-first, then IndexedDB when offline / unauthenticated.
// =============================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authToken(): string | null {
  return localStorage.getItem('akt_auth_token');
}

/** Is this a key the SERVER (or a remote URL) owns, vs a local idb:// blob? */
export function isServerKey(key: string | null | undefined): boolean {
  return !!key && (key.startsWith('blob:') || key.startsWith('http://') || key.startsWith('https://'));
}

/**
 * Upload a NEW document's gzip-base64 bytes to the server blob store and return
 * its `blob:` key, or null if the server is unavailable / the user isn't logged
 * in (caller then falls back to the local IndexedDB store).
 */
export async function putServerBlob(
  base64: string,
  accountOwnerId?: number | string | null,
): Promise<string | null> {
  const token = authToken();
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/dms/blob`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ base64, account_owner_id: accountOwnerId ?? null }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return typeof json?.key === 'string' ? json.key : null;
  } catch {
    return null;
  }
}

/** Fetch a document's gzip-base64 bytes for a server/remote `key`, or null. */
export async function getServerBlob(key: string): Promise<string | null> {
  const token = authToken();
  try {
    // A real remote URL (future Cloud Storage) is fetched directly.
    if (key.startsWith('http://') || key.startsWith('https://')) {
      const res = await fetch(key);
      if (!res.ok) return null;
      return await res.text();
    }
    const url = `${API_BASE}/dms/blob?key=${encodeURIComponent(key)}` +
      (token ? `&token=${encodeURIComponent(token)}` : '');
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) return null;
    const json = await res.json();
    return typeof json?.base64 === 'string' ? json.base64 : null;
  } catch {
    return null;
  }
}

/** Best-effort delete of a server blob (never block a doc delete on this). */
export async function deleteServerBlob(key: string): Promise<void> {
  const token = authToken();
  if (!token || !key.startsWith('blob:')) return;
  try {
    await fetch(`${API_BASE}/dms/blob?key=${encodeURIComponent(key)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch { /* ignore */ }
}
